import { Injectable } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { baseUrl, options, optionsReset } from "../environments/environment";
import { Observable, tap } from 'rxjs';
import { MediaService } from './media.Service';
import { ReportModel } from './question.service';
import { EstimateModel } from './panel.Service';
import { FinancialService, Payment, PaymentType, ReferenceType } from './finacial.Service';
import { WindowModel } from './authentication.Service';
import { optionsResetForUpdate } from '../environments/environment.prod';
import "reflect-metadata";
import { plainToClass, Type } from 'class-transformer';
import * as moment from 'moment-timezone';

@Injectable()
export class SurveyService {
  constructor(private http: HttpClient) {
  }

  updateBasicDetails(model: SurveyModel): Observable<any> {
    return this.http.put(baseUrl + 'Survey/UpdateBasicDetails', model, options);
  }

  updateReportModel(id: number, model: ReportModel, updateTitle: boolean) {
    return this.http.put(baseUrl + 'Survey/UpdateReportModel?id=' + id + '&updateTitle=' + updateTitle, model, optionsResetForUpdate);
  }

  createBlankSurvey(model: SurveyModel) {
    return this.http.post(baseUrl + 'Survey/CreateBlankSurvey', model, optionsResetForUpdate);
  }

  getSurveyCategories(): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveyCategories', options);
  }

  getSurveySummaryByCategory(category: string): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveySummariesByCategory?id=' + category, options);
  }

  getSurveySummaries(): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveySummaries', options);
  }

  getSurveyTotals(): Observable<number[]> {
    return this.http.get<number[]>(baseUrl + 'Survey/GetSurveyTotals', options);
  }

  getSurveySummariesForUser(userid: string): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveySummariesForUser?userid=' + userid, options);
  }

  getSurveyCount(): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveyCount', options);
  }

  getPagedSurveySummaries(status: number, searchKey: string, page: number, pageSize: number): Observable<SurveySummaryModel[]> {
    return this.http.post<SurveySummaryModel[]>(baseUrl + 'Survey/GetSurveySummariesPaged?status=' + status + '&page=' + page + '&pageSize=' + pageSize, JSON.stringify({ "Name": searchKey }), optionsReset);
  }

  getSurveySummariesForSystem(): Observable<any> {
    return this.http.get<SurveySummaryModel[]>(baseUrl + 'Survey/GetSurveySummariesForSystem', options);
  }

  getSurveySummary(id: number): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveySummary?id=' + id, optionsReset);
  }

  getSurveyDetail(id: number, edit: boolean): Observable<SurveyModel> {
    return this.http.get<SurveyModel>(baseUrl + 'Survey/GetSurveyDetail?id=' + id + "&edit=" + edit, optionsReset);
  }

  getSurveyCompletes(id: number, panelId: number): Observable<any> {
    return this.http.get(baseUrl + 'Survey/GetSurveyCompletes?id=' + id + '&panelId=' + panelId, options);
  }

  getSurveyDetailAdmin(id: number): Observable<SurveyModel> {
    return this.http.get<SurveyModel>(baseUrl + 'Survey/GetSurveyDetailAdmin?id=' + id, options);
  }

  checkSurvey(name: string, username: string): Observable<any> {
    return this.http.get<any>(baseUrl + 'Survey/CheckSurvey?name=' + name + "&username=" + username, options);
  }

  transferSurvey(id: number, name: string, username: string, orgname: string): Observable<any> {
    return this.http.get<any>(baseUrl + 'Survey/TransferSurvey?id=' + id + '&name=' + name + '&username=' + username + "&orgname=" + orgname, optionsResetForUpdate);
  }

  deleteSurvey(id: number): Observable<any> {
    return this.http.delete(baseUrl + 'Survey/DeleteSurvey?SurveyIDs=' + id, optionsResetForUpdate);
  }

  copySurvey(id: number): Observable<any> {
    return this.http.get(baseUrl + 'Survey/CopySurvey?surveyID=' + id, optionsResetForUpdate);
  }

  copyTemplate(id: number): Observable<any> {
    return this.http.get(baseUrl + 'Survey/CopyTemplate?surveyID=' + id, optionsResetForUpdate);
  }

  setPanel(surveyId: number, panelId: number) {
    this.http.patch(baseUrl + 'Survey/SetPanel?surveyId=' + surveyId + '&panelId=' + panelId, optionsResetForUpdate).subscribe();
  }

  saveJobMaster(model: JobMasterModel): Observable<any> {
    return this.http.put<JobMasterModel>(baseUrl + 'Survey/UpdateJobMaster', model, optionsResetForUpdate);
  }

  saveJobMasterForDistribution(model: JobMasterModel): Observable<DistributionModel> {
    return this.http.put<DistributionModel>(baseUrl + 'Survey/UpdateJobMasterForDistribution', model, optionsResetForUpdate);
  }

  removeJobMaster(model: SurveyModel): Observable<any> {
    return this.http.patch(baseUrl + 'Survey/RemoveJobMaster', model, optionsResetForUpdate);
  }

  getDeliveryStatsSinceBeginning(id: number): Observable<DeliveryItem[]> {
    return this.http.get<DeliveryItem[]>(baseUrl + 'Survey/GetDeliveryStatsFromTheBeginning?surveyId=' + id)
  }

  exportSurveys(userid: number): Observable<any> {
    return this.http.get<any>(baseUrl + 'Survey/ExportSurveys?userid=' + userid, options);
  }

  importSurveys(filename: string, selections: ImportProjectSelections, userid: number): Observable<any> {
    return this.http.post(baseUrl + 'Survey/ImportSurveys?filename=' + filename + '&userid=' + userid, selections, optionsResetForUpdate);
  }

  makePayment(model: SurveyModel, estimate: EstimateModel) {
    let payment = new Payment();
    payment.EndingIn = 0;
    payment.Expires = new Date();
    payment.Description = "Payment for survey ";
    payment.TypeID = PaymentType.Credit;
    payment.BillingEmail = "";
    payment.AlternateBillingEmail = "";
    payment.CompanyName = "";
    payment.PurchaseOrderNumber = "";
    payment.Address = "";
    payment.Town = "";
    payment.County = "";
    payment.PostCode = "";
    payment.Country = "";
    payment.VatRegistrationNumber = "";
    payment.Amount = estimate.Total;
    payment.InvoiceLines = FinancialService.buildInvoiceLines(estimate);
    payment.Reference = model.SurveyID;
    payment.ReferenceTypeID = ReferenceType.Survey;
    return this.http.post(baseUrl + 'finance/ChargeBalanceForReference', payment, options);
  }

  makeLive(model: SurveyModel) {
    return this.http.put<SurveyModel>(baseUrl + 'Survey/MakeLive', model, optionsResetForUpdate);
  }

  abandonSurvey(id: number) {
    return this.http.get<SurveyModel>(baseUrl + 'Survey/AbandonSurvey?id=' + id, optionsResetForUpdate);
  }

  relaunchSurvey(id: number) {
    return this.http.get<SurveyModel>(baseUrl + 'Survey/RelaunchSurvey?id=' + id, optionsResetForUpdate);
  }

  getAnonymousLink(id: number) {
    return this.http.get<string>(baseUrl + 'Survey/GetAnonymousLink?id=' + id, optionsResetForUpdate);
  }

  getSurveyTesetLink(id: number): Observable<any> {
    return this.http.get<DistributionModel>(baseUrl + 'Survey/GetSurveyTestUrls?id=' + id, options);
  }
}

export class DistributionModel {
  Sid: string = "";
  TinyUrl: string = "";
  JobMasterId: number = 0;
}

export class PeriodStatusTotal {
  Period: string = "";
  Total: StatusTotal = new StatusTotal();
}

export class StatusTotal {
  Status: number = 0;
  Total: number = 0;
}

export class SurveyModel {
  SurveyID: number = 0;
  UserID: number = 0;
  Name: string = "";
  Description: string = "";
  Status: number = 1;
  Type: number = 0;
  MediaID: number = 0;
  ScriptID: number = 0;
  IsDeleted: boolean = false;
  IsOffline: boolean = false;
  RewardPoints: number = 0;
  IsCapi: boolean = false;
  @Type(() => Date)
  CreatedDate: Date = new Date();
  @Type(() => Date)
  LastUpdatedDate: Date = new Date();
  EstimatedTime: number = 0;
  Occurences: number = 0;
  @Type(() => Date)
  DeadLine: Date = new Date();
  Link: string = "";
  ThirdParty: boolean = false;
  IsProfileSurvey: boolean = false;
  SurveyReference: string = "";
  IsLive: boolean = false;
  PanelID: number = 0;
  JobMaster: JobMasterModel | null = null;
  PanelSize: number = 0;
  Completed: number = 0;
  QuestionCount: number = 0;
  SearchTags: string = "";
  CintId: number = 0;
  Reference: PanellistReference = new PanellistReference();
  Sid: string = "";
  ReportModel: string = "";
  Channel: number = -1;
  Totals: StatusTotal[] = [];
  WindowId: number = 0;
  Windows: WindowModel[] | null = [];

  public plainToClass() {
    this.JobMaster = plainToClass(JobMasterModel, this.JobMaster);
    this.Reference = plainToClass(PanellistReference, this.Reference);
    for (let i = 0; i < this.Windows?.length; i++) {
      this.Windows[i] = plainToClass(WindowModel, this.Windows[i]);
    }
  }

  public static getSurveyObject(result: any): SurveyModel {
    let temp = plainToClass(SurveyModel, result);
    temp.plainToClass();
    return temp;
  }
}

export class SurveyReferenceModel {
  SurveyID: number = 0;
  Channel: number = 0;
  Script: string = "";
}

export class SurveySummaryModel {

  SurveyID: number = 0;
  UserID: number = 0;
  Name: string = "";
  Description: string = "";
  SurveyReference: string = "";
  SearchTags: string = "";
  Status: number = 0;
  Type: number = 0;
  Channel: number = 0;
  @Type(() => Date)
  Created: Date = new Date();
  @Type(() => Date)
  LastUpdated: Date = new Date();
  MediaID: number = 0;
  QuestionCount: number = 0;
  PanellistCount: number = 0;
  CompletionCount: number = 0;
  ReportModel: string = "";
  Script: string = "";
  @Type(() => Date)
  Started: Date = new Date();
  @Type(() => Date)
  Ended: Date = new Date();
  @Type(() => Date)
  Launched: Date = new Date();
  RepeatSchedule: boolean = false;
  SurveyReferences: SurveyReferenceModel[] = [];
  StatusTotals: StatusTotal[] = [];
  MediaLoading?: Observable<string>

  get Media(): string {
    if (this._media == "" && this.MediaID > 0 && !this._gettingMedia) {
      this._gettingMedia = true;
      this.getMediaForSurvey()
    }

    return this._media;
  }
  set Media(value: string) {
    this._media = value;
  }
  private _media: string = "";

  getMediaForSurvey(): Observable<string> | null {
    if (this.MediaLoading != undefined) {
      return this.MediaLoading;
    }

    if (this.mediaService == null) {
      return null;
    }

    if (this.MediaID == 0) {
      this._media = "";
      this.MediaLoading = new Observable<string>(result => { result.next("x"); });
      return this.MediaLoading;
    }

    this.MediaLoading = this.mediaService.getMediaById(this.MediaID).pipe(tap<string>(
      result => {
        this._media = result
      },
      error => {
        this._media = ""
      }
    ));
    return this.MediaLoading;
  }

  public static copy(result: SurveySummaryModel, mediaService?: MediaService) {
    const model = new SurveySummaryModel(mediaService);
    model.SurveyID = result.SurveyID;
    model.UserID = result.UserID;
    model.Name = result.Name;
    model.Description = result.Description;
    model.SurveyReference = result.SurveyReference;
    model.SearchTags = result.SearchTags;
    model.Status = result.Status;
    model.Type = result.Type;
    model.Created = result.Created;
    model.LastUpdated = result.LastUpdated;
    model.MediaID = result.MediaID;
    model.SurveyReferences = result.SurveyReferences;
    model.Script = result.Script;
    return model;
  }


  private _gettingMedia = false;
  private mediaService?: MediaService;

  constructor(mediaService?: MediaService) {
    this.mediaService = mediaService;
  }
}

export class JobMasterModel {
  JobMasterId: number = 0;
  SurveyID: number = 0;
  Name: string = "";
  CronExpression: string = "";
  Adjustment: number = 0;
  @Type(() => Date)
  Start: Date = new Date();
  @Type(() => Date)
  End: Date = new Date();
  @Type(() => Date)
  Launched: Date = new Date();
  IsActive: boolean = false;
  IsDeleted: boolean = false;
  CanMissfire: boolean = false;
  ChannelID: number = 0;
  Subject: string = "";
  Content: string = "";
  JobTypeID: number = 0;

  get Crons(): Schedule[] {
    if (this._crons?.length > 0) {
      return this._crons;
    }

    try {
      let cronSchedules = JSON.parse(this.CronExpression);
      this._crons = [];
      for (let i = 0; i < cronSchedules.Schedules.length; i++) {
        this._crons.push(plainToClass(Schedule, cronSchedules.Schedules[i]));
      }

      if (this._crons.length > 0) {
        return this._crons;
      }
    }
    catch (error) {
      let a = error;

    }

    let cron = new Schedule();
    cron.timezone = this.Name;
    cron.start = this.Start;
    cron.end = this.End;
    cron.cron = this.CronExpression;
    this._crons = [];
    this._crons.push(cron);
    this.updateCrons(this._crons);
    return this._crons;
  }
  set Crons(value: Schedule[]) {
    this.updateCrons(value);
  }
  private _crons: Schedule[] = null

  updateCrons(value: Schedule[]) {
    this._crons = value;
    let cronSchedules = new CronSchedules();
    cronSchedules.Schedules = value;
    this.CronExpression = JSON.stringify(cronSchedules);
  }
}

export class CronSchedules {
  //@ValidateNested()
  @Type(() => Schedule)
  Schedules: Schedule[] = [];
}

export class Schedule {
  timezone: string = ""; // moment.tz.guess();
  startingSelected: boolean = false;
  @Type(() => Date)
  start: Date = new Date();
  @Type(() => Date)
  end: Date = new Date(new Date().getDate() + 3);
  cron: string;
  at: string = "";

  constructor(timezone?: string, startingSelected?: boolean, start?: Date, end?: Date, cron?: string) {
    if (timezone) {
      this.timezone = timezone;
    }

    if (startingSelected) {
      this.startingSelected = startingSelected;
    }

    if (start) {
      this.start = start;
    }

    if (end) {
      this.end = end;
    }

    if (cron) {
      this.cron = cron;
    }
  }

  get offset(): number {
    let a = moment.tz(this.timezone);
    return moment.tz(this.timezone).utcOffset();
  }
}

export class PanellistReference {
  PanellistReferenceID: number = 0;
  Created: Date = new Date();
  Expires: Date = new Date();
  SurveyID: number = 0;
  Serial: number = 0;
  InviteID: number = 0;
  Guid: string = "";
  BatchID: number = 0;
  StatisID: number = 0;
  PanellistID: number = 0;
}

export class DeliveryItem {
  SurveyId: number = 0;
  Period: Date = new Date();
  Label: string = "";
  StatusValue: number = 0;
  Total: number = 0;
}

export class ImportProjectSelections {
  AllSurveys: boolean;
  Criteria: ProjectSelectionCriteria;
  Surveys: ImportProjectSelection[] = [];
}

export class ImportProjectSelection {
  SurveyId: number;
  Criteria: ProjectSelectionCriteria;
}

export class ProjectSelectionCriteria {
  public Respondents: boolean;
  public DataIO: boolean;
  public JobMaster: boolean
}

function OneToMany(arg0: (type: any) => any, arg1: (piece: any) => any): (target: CronSchedules, propertyKey: "Schedules") => void {
    throw new Error('Function not implemented.');
}

function ValidateNested(): (target: CronSchedules, propertyKey: "Schedules") => void {
    throw new Error('Function not implemented.');
}
